import { useState, useRef } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import { useFloating, offset, shift, arrow, autoUpdate, useInteractions, useHover, useFocus, useRole } from '@floating-ui/react-dom-interactions';
import { jsxs, jsx } from 'react/jsx-runtime';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var themes = ['light', 'dark'];
var placements = ['top-start', 'top', 'top-end', 'bottom-start', 'bottom', 'bottom-end', 'right', 'left'];
var statuses = ['default', 'warning', 'danger'];

var _excluded = ["className", "children", "content", "theme", "status", "placement"];
var prefix = 'forno-tooltip';
/**
 * Renders a `<Tooltip />` component
 * @param {Object} defaultProps
 * @param {string} [props.className] - Custom class name
 * @param {children} props.children - The child / children of this component
 * @param {content} [props.content] - The tooltip content, must be a node
 * @param {theme} [props.theme] - The theme of the tooltip (dark or light)
 * @param {status} [props.status] - The status of the tooltip (default, warning, danger)
 * @param {placement} [props.placement] - The placement of the tooltip [top, top-start, top-end,
 *                                        right, bottom, bottom-start, bottom-end, left]
 */

function Tooltip(_ref) {
  var className = _ref.className,
      children = _ref.children,
      content = _ref.content,
      theme = _ref.theme,
      status = _ref.status,
      placement = _ref.placement,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      open = _useState2[0],
      setOpen = _useState2[1];

  var customClassName = classnames(prefix, className);
  var arrowRef = useRef(null); // Tooltip Parameters

  var _useFloating = useFloating({
    open: open,
    onOpenChange: setOpen,
    middleware: [offset(16), shift({
      padding: 8
    }), arrow({
      element: arrowRef
    })],
    placement: placement,
    whileElementsMounted: autoUpdate
  }),
      x = _useFloating.x,
      y = _useFloating.y,
      reference = _useFloating.reference,
      floating = _useFloating.floating,
      strategy = _useFloating.strategy,
      context = _useFloating.context;

  var _useInteractions = useInteractions([useHover(context, {
    restMs: 40
  }), useFocus(context), useRole(context)]),
      getReferenceProps = _useInteractions.getReferenceProps,
      getFloatingProps = _useInteractions.getFloatingProps;

  var tooltipPosition = {
    position: strategy,
    top: y !== null || y !== undefined ? y : 0,
    left: x !== null || x !== undefined ? x : 0
  };
  return /*#__PURE__*/jsxs("div", {
    className: "".concat(prefix, "-wrapper"),
    "data-theme": theme,
    "data-status": status,
    "data-testid": "Tooltip",
    children: [/*#__PURE__*/jsx("div", _objectSpread2(_objectSpread2({
      className: "".concat(prefix, "-children-container"),
      ref: reference
    }, getReferenceProps()), {}, {
      children: children
    })), /*#__PURE__*/jsxs("div", _objectSpread2(_objectSpread2(_objectSpread2({
      ref: floating,
      style: tooltipPosition,
      className: customClassName,
      "data-open": open
    }, rest), getFloatingProps()), {}, {
      children: [content, /*#__PURE__*/jsx("div", {
        ref: arrowRef,
        className: "".concat(prefix, "-arrow"),
        "data-placement": placement,
        "data-open": open
      })]
    }))]
  });
}
Tooltip.propTypes = {
  className: propTypes.string,
  children: propTypes.node.isRequired,
  content: propTypes.node.isRequired,
  theme: propTypes.oneOf(themes),
  status: propTypes.oneOf(statuses),
  placement: propTypes.oneOf(placements)
};
Tooltip.defaultProps = {
  className: '',
  theme: 'light',
  status: 'default',
  placement: 'bottom'
};

export { Tooltip as default };
